<script setup lang="ts">
import { FileText, Image, RefreshCw, X } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import type { FileAttachmentDraft } from '~/composables/files/useFileAttachments';

withDefaults(defineProps<{
    drafts: FileAttachmentDraft[];
    disabled?: boolean;
    readyLabel?: string;
    loading?: boolean;
    emptyText?: string;
}>(), {
    disabled: false,
    loading: false,
    emptyText: '',
});
defineEmits<{ remove: [id: string]; retry: [id: string] }>();
</script>

<template>
    <div v-if="loading && !drafts.length" class="space-y-2" aria-label="Preparing attachments">
        <Skeleton class="h-12 w-full rounded-xl" />
    </div>
    <div v-else-if="drafts.length" class="space-y-2">
        <div v-for="draft in drafts" :key="draft.id" class="flex items-center gap-3 rounded-xl bg-muted/65 px-3 py-2">
            <Image v-if="draft.file.type.startsWith('image/')" class="size-4 shrink-0" />
            <FileText v-else class="size-4 shrink-0" />
            <div class="min-w-0 flex-1">
                <p class="truncate text-xs font-medium">{{ draft.file.name }}</p>
                <Progress v-if="draft.status === 'uploading'" :model-value="draft.progress" class="mt-1 h-1" />
                <p v-else-if="draft.status === 'error'" class="mt-0.5 text-[11px] text-destructive">{{ draft.error }}</p>
                <p v-else class="mt-0.5 text-[11px] text-muted-foreground">{{ readyLabel || 'Ready to attach' }}</p>
            </div>
            <Button v-if="draft.status === 'error'" type="button" variant="ghost" size="icon" class="size-7" :disabled="disabled" aria-label="Retry upload" @click="$emit('retry', draft.id)">
                <RefreshCw class="size-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" class="size-7" :disabled="disabled || draft.status === 'uploading'" aria-label="Remove attachment" @click="$emit('remove', draft.id)">
                <X class="size-3.5" />
            </Button>
        </div>
    </div>
    <p v-else-if="emptyText" class="text-xs text-muted-foreground">{{ emptyText }}</p>
</template>
